import React, { useState, useMemo } from 'react'
import { Modal, Button, Table, Spinner, Badge, Form, Alert } from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import { useQuery } from '@apollo/client'
import {
  GET_CARRIER_IMPORT_BATCH,
  GET_CARRIER_IMPORT_BATCH_UNMATCHED,
} from '../../../gql/queries/carrierImportQueries'
import moment from 'moment'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'

interface ModalViewCarrierImportBatchProps {
  show: boolean
  onClose: () => void
  batchId: string
  policyStatuses: {code: string; title: string; color?: string}[]
}

const M = defineMessages({
  title: {id: 'policies.list.viewBatch.title', defaultMessage: 'Carrier Import Batch Details'},
  sectionBatchInfo: {id: 'policies.list.viewBatch.section.batchInfo', defaultMessage: 'Batch Information'},
  labelCarrier: {id: 'policies.list.viewBatch.label.carrier', defaultMessage: 'Carrier:'},
  labelAsOfDate: {id: 'policies.list.viewBatch.label.asOfDate', defaultMessage: 'As-of Date:'},
  labelFilename: {id: 'policies.list.viewBatch.label.filename', defaultMessage: 'Filename:'},
  labelUploaded: {id: 'policies.list.viewBatch.label.uploaded', defaultMessage: 'Uploaded:'},
  labelUploadedBy: {id: 'policies.list.viewBatch.label.uploadedBy', defaultMessage: 'Uploaded By:'},
  emptyNoUnmatched: {id: 'policies.list.viewBatch.emptyNoUnmatched', defaultMessage: 'No unmatched rows in this batch.'},
  batchNotFound: {id: 'policies.list.viewBatch.batchNotFound', defaultMessage: 'Batch not found.'},
  prev: {id: 'policies.list.viewBatch.prev', defaultMessage: 'Previous'},
  next: {id: 'policies.list.viewBatch.next', defaultMessage: 'Next'},
  // Reused from the carrier-import modal catalog
  statProcessed: {id: 'policies.list.byCarrier.stat.processed', defaultMessage: 'Processed:'},
  statMatched: {id: 'policies.list.byCarrier.stat.matched', defaultMessage: 'Matched:'},
  statUnmatched: {id: 'policies.list.byCarrier.stat.unmatched', defaultMessage: 'Unmatched:'},
  statErrors: {id: 'policies.list.byCarrier.stat.errors', defaultMessage: 'Errors:'},
  unmatchedRows: {id: 'policies.list.byCarrier.unmatchedRows', defaultMessage: 'Unmatched Rows ({count})'},
  ofTotal: {id: 'policies.list.byCarrier.ofTotal', defaultMessage: '{shown} of {total}'},
  exportCsv: {id: 'policies.list.byCarrier.exportCsv', defaultMessage: 'Export Unmatched CSV'},
  phSearch: {id: 'policies.list.byCarrier.phSearch', defaultMessage: 'Search by Member ID or Name...'},
  thRow: {id: 'policies.list.byCarrier.th.row', defaultMessage: 'Row'},
  thMemberId: {id: 'policies.list.byCarrier.th.memberId', defaultMessage: 'Member ID'},
  thName: {id: 'policies.list.byCarrier.th.name', defaultMessage: 'Name'},
  thCarrierStatus: {id: 'policies.list.byCarrier.th.carrierStatus', defaultMessage: 'Carrier Status'},
  thSystemStatus: {id: 'policies.list.byCarrier.th.systemStatus', defaultMessage: 'System Status'},
  thAgent: {id: 'policies.list.byCarrier.th.agent', defaultMessage: 'Agent'},
  thReason: {id: 'policies.list.byCarrier.th.reason', defaultMessage: 'Reason'},
  pageOf: {id: 'policies.list.byCarrier.pageOf', defaultMessage: 'Page {page} of {total}'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
})

export const ModalViewCarrierImportBatch: React.FC<ModalViewCarrierImportBatchProps> = ({
  show,
  onClose,
  batchId,
  policyStatuses,
}) => {
  const intl = useIntl()
  const naValue = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const dash = '-' // non-translatable empty-cell placeholder (hoisted)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const rowsPerPage = 25

  const { data: batchData, loading: batchLoading } = useQuery(GET_CARRIER_IMPORT_BATCH, {
    variables: { id: batchId },
    skip: !batchId,
  })

  const { data: unmatchedData, loading: unmatchedLoading } = useQuery(
    GET_CARRIER_IMPORT_BATCH_UNMATCHED,
    {
      variables: { batchId },
      skip: !batchId,
    }
  )

  const batch = batchData?.carrierImportBatch
  const unmatchedRows = useMemo<CarrierImportUnmatchedRow[]>(
    () => unmatchedData?.carrierImportBatchUnmatched || [],
    [unmatchedData?.carrierImportBatchUnmatched]
  )

  const headerOrder = useMemo(() => {
    const meta = batch?.metadata
    if (!meta) return undefined
    if (Array.isArray(meta)) return meta
    if (typeof meta === 'string') {
      try {
        const parsed = JSON.parse(meta)
        if (parsed && Array.isArray(parsed.headers)) return parsed.headers
      } catch {
        return undefined
      }
    }
    if (typeof meta === 'object' && Array.isArray((meta as any).headers)) {
      return (meta as any).headers
    }
    return undefined
  }, [batch?.metadata])

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  // Filter rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return unmatchedRows

    const query = searchQuery.toLowerCase()
    return unmatchedRows.filter(
      (row) =>
        row.memberId?.toLowerCase().includes(query) || row.name?.toLowerCase().includes(query)
    )
  }, [unmatchedRows, searchQuery])

  // Paginate filtered rows
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage)
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, currentPage])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  return (
    <Modal show={show} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {batchLoading ? (
          <div className='d-flex justify-content-center p-4'>
            <Spinner animation='border' />
          </div>
        ) : batch ? (
          <>
            <div className='mb-4'>
              <h6>{intl.formatMessage(M.sectionBatchInfo)}</h6>
              <div className='row'>
                <div className='col-md-6'>
                  <p>
                    <strong>{intl.formatMessage(M.labelCarrier)}</strong> {batch.carrier.name}
                  </p>
                  <p>
                    <strong>{intl.formatMessage(M.labelAsOfDate)}</strong> {batch.asOfDate || naValue}
                  </p>
                  <p>
                    <strong>{intl.formatMessage(M.labelFilename)}</strong> {batch.originalFilename}
                  </p>
                </div>
                <div className='col-md-6'>
                  <p>
                    <strong>{intl.formatMessage(M.labelUploaded)}</strong> {moment(batch.uploadedAt).format('MM/DD/YYYY hh:mm A')}
                  </p>
                  <p>
                    <strong>{intl.formatMessage(M.labelUploadedBy)}</strong> {batch.uploadedBy.firstName} {batch.uploadedBy.lastName}
                  </p>
                </div>
              </div>

              <div className='d-flex gap-3 mb-3'>
                <div>
                  <strong>{intl.formatMessage(M.statProcessed)}</strong> <Badge bg='secondary'>{batch.processedCount}</Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statMatched)}</strong> <Badge bg='success'>{batch.matchedCount}</Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statUnmatched)}</strong> <Badge bg='warning' text='dark'>
                    {batch.unmatchedCount}
                  </Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statErrors)}</strong> <Badge bg='danger'>{batch.errorsCount}</Badge>
                </div>
              </div>
            </div>

            {unmatchedLoading ? (
              <div className='d-flex justify-content-center p-4'>
                <Spinner animation='border' size='sm' />
              </div>
            ) : unmatchedRows.length > 0 ? (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>
                    {intl.formatMessage(M.unmatchedRows, {
                      count: searchQuery.trim()
                        ? intl.formatMessage(M.ofTotal, {shown: filteredRows.length, total: unmatchedRows.length})
                        : filteredRows.length,
                    })}
                  </h6>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() =>
                      exportUnmatchedCsv(
                        intl,
                        unmatchedRows,
                        statusTitleByCode,
                        `carrier_import_batch_${batchId}_unmatched`,
                        headerOrder
                      )
                    }
                  >
                    {intl.formatMessage(M.exportCsv)}
                  </Button>
                </div>

                <Form.Control
                  type='text'
                  placeholder={intl.formatMessage(M.phSearch)}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='mb-2'
                  size='sm'
                />

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover size='sm'>
                    <thead>
                      <tr>
                        <th>{intl.formatMessage(M.thRow)}</th>
                        <th>{intl.formatMessage(M.thMemberId)}</th>
                        <th>{intl.formatMessage(M.thName)}</th>
                        <th>{intl.formatMessage(M.thCarrierStatus)}</th>
                        <th>{intl.formatMessage(M.thSystemStatus)}</th>
                        <th>{intl.formatMessage(M.thAgent)}</th>
                        <th>{intl.formatMessage(M.thReason)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, idx) => (
                        <tr key={idx}>
                        <td>{row.rowNumber}</td>
                        <td>{row.memberId || dash}</td>
                        <td>{row.name || dash}</td>
                        <td>{row.carrierStatusRaw || row.carrierStatusGeneric || dash}</td>
                        <td>
                          {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                            <BadgeStatePolicy
                              status={statusTitleByCode[row.systemPolicyStatus]}
                              color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                            />
                          ) : (
                            dash
                          )}
                        </td>
                        <td>
                          {row.systemAgentName || row.systemAgentEmail ? (
                            <>
                              {row.systemAgentName || dash}
                              {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                            </>
                          ) : (
                            dash
                          )}
                        </td>
                        <td>
                          <small>{row.reason || dash}</small>
                        </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center mt-2'>
                    <span className='text-muted'>
                      {intl.formatMessage(M.pageOf, {page: currentPage, total: totalPages})}
                    </span>
                    <div>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className='me-2'
                      >
                        {intl.formatMessage(M.prev)}
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      >
                        {intl.formatMessage(M.next)}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Alert variant='info'>{intl.formatMessage(M.emptyNoUnmatched)}</Alert>
            )}
          </>
        ) : (
          <Alert variant='danger'>{intl.formatMessage(M.batchNotFound)}</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {intl.formatMessage(M.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
